/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Conditional visibility (`visibleIf`) helpers for ui:tree leaves.
 *
 * A top-level ui:tree leaf may declare
 * `visibleIf: {valuePath: <path from passport root>, equals: <primitive>}`.
 * The field is only shown (edit and view mode) while the passport value at
 * `valuePath` is strictly equal to `equals`. When a field becomes hidden in
 * edit mode, its value is cleared (set to `null`, which makes the backend
 * delete it) so no stale value is persisted.
 */

import {
  FormCategory,
  FormFieldAttribute,
  VisibleIfCondition,
} from '../../../models/FormCategory';
import {GeneralDataHandlingService} from '../../../services/general.data-handling.service';

/** A field that carries a `visibleIf` rule, keyed by its own value path. */
export type ConditionalField = {
  valuePath: string;
  visibleIf: VisibleIfCondition;
};

// Stateless; shared so path semantics match the rest of the webapp.
const dataHandling = new GeneralDataHandlingService();

function getValueByPath(data: unknown, path: string): unknown {
  return dataHandling.getValueByPath(data, path);
}

/** Structural equality that ignores object key order. */
function isDeepEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (
    a === null ||
    b === null ||
    typeof a !== 'object' ||
    typeof b !== 'object' ||
    Array.isArray(a) !== Array.isArray(b)
  ) {
    return false;
  }
  const aRecord = a as Record<string, unknown>;
  const bRecord = b as Record<string, unknown>;
  const aKeys = Object.keys(aRecord);
  return (
    aKeys.length === Object.keys(bRecord).length &&
    aKeys.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(bRecord, key) &&
        isDeepEqual(aRecord[key], bRecord[key]),
    )
  );
}

/**
 * Returns whether a field with the given `visibleIf` rule is visible for `data`.
 * Fields without a rule are always visible.
 *
 * @param visibleIf - The field's rule, if any.
 * @param data - The passport data (`PassportDto.data`).
 */
export function isVisibleIfSatisfied(
  visibleIf: VisibleIfCondition | undefined,
  data: unknown,
): boolean {
  if (!visibleIf) {
    return true;
  }
  return getValueByPath(data, visibleIf.valuePath) === visibleIf.equals;
}

/**
 * Collects all fields with a `visibleIf` rule from the mapped form categories.
 *
 * @param categories - The mapped categories (`loadMappedSchema$()` result).
 */
export function collectConditionalFields(
  categories: FormCategory[],
): ConditionalField[] {
  return categories
    .flatMap((category) => category.attributes ?? [])
    .flatMap((subCategory) => subCategory.attributes ?? [])
    .filter(
      (
        attribute,
      ): attribute is FormFieldAttribute & {visibleIf: VisibleIfCondition} =>
        attribute.visibleIf !== undefined,
    )
    .map(({valuePath, visibleIf}) => ({valuePath, visibleIf}));
}

/**
 * Clears (sets to `null`) the values of conditional fields that are hidden in
 * `data`, when either
 * - the field was visible in `previousData` (the controlling value changed away), or
 * - the hidden field's value differs from `previousData` (it was edited while,
 *   or right before, becoming hidden).
 *
 * Hidden values that are unchanged since `previousData` (e.g. stale values of
 * passports created before the rule existed) are left untouched.
 *
 * Repeats until nothing changes, so a dependent of a field that was itself
 * cleared (chained rules) is cleared too, whatever the order of `fields`.
 *
 * Mutates `data` in place.
 *
 * @param fields - The conditional fields of the passport's schema.
 * @param previousData - The last persisted passport data.
 * @param data - The passport data about to be persisted.
 * @returns The value paths that were cleared.
 */
export function clearHiddenConditionalFields(
  fields: ConditionalField[],
  previousData: unknown,
  data: unknown,
): string[] {
  const cleared: string[] = [];
  let clearedInPass = true;
  while (clearedInPass) {
    clearedInPass = false;
    for (const {valuePath, visibleIf} of fields) {
      const current = getValueByPath(data, valuePath);
      if (
        current === null ||
        current === undefined ||
        isVisibleIfSatisfied(visibleIf, data)
      ) {
        continue;
      }
      const wasVisible = isVisibleIfSatisfied(visibleIf, previousData);
      const changed = !isDeepEqual(
        current,
        getValueByPath(previousData, valuePath),
      );
      if (wasVisible || changed) {
        dataHandling.setValueByPath(data, valuePath, null);
        cleared.push(valuePath);
        clearedInPass = true;
      }
    }
  }
  return cleared;
}
